import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Select,
  Option,
  Grid,
  Card,
  Avatar,
  Chip
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import StyledTable from '../../components/StyledTable';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    project_number: '',
    name: '',
    client_id: '',
    status: 'In Progress',
    latitude: '',
    longitude: ''
  });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        project_locations (
          latitude,
          longitude
        )
      `);
    if (!error) setProjects(data);
    setLoading(false);
  };

  const fetchClients = async () => {
    const { data, error } = await supabase.from('clients').select('id, name');
    if (!error) setClients(data);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (editMode) {
      await supabase.from('projects').update({
        name: formData.name,
        project_number: formData.project_number,
        client_id: formData.client_id,
        status: formData.status
      }).eq('id', formData.id);

      await supabase.from('project_locations')
        .upsert({
          project_id: formData.id,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude)
        }, { onConflict: ['project_id'] });
    } else {
      // Insert new project and fetch the inserted row (with id)
      const { data: newProject, error } = await supabase.from('projects').insert([{
        name: formData.name,
        project_number: formData.project_number,
        client_id: formData.client_id,
        status: formData.status || 'In Progress'
      }]).select().single();

      if (newProject && !error) {
        await supabase.from('project_locations').insert([{
          project_id: newProject.id,
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude)
        }]);

        // Fetch all project_templates
        const { data: templates } = await supabase.from('project_templates').select('*');
        // For each template, insert a record into project_records using new project id
        const now = new Date().toISOString();
        const inserts = (templates || []).map(template => ({
          project_id: newProject.id,
          company_id: newProject.company_id,
          client_id: newProject.client_id,
          name: template.name,
          record_type: template.record_type,
          agency: template.default_agency,
          start_date: now
        }));
        if (inserts.length) {
          const { error: insertError } = await supabase.from('project_records').insert(inserts);
          if (insertError) {
            console.error("Failed to insert project records:", insertError);
          }
        }
      }
    }
    setOpen(false);
    fetchProjects();
  };

  const handleEdit = async (project) => {
    const { data: location, error } = await supabase
      .from('project_locations')
      .select('latitude, longitude')
      .eq('project_id', project.id)
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching project location:", error);
    }

    setFormData({
      id: project.id,
      project_number: project.project_number || '',
      name: project.name || '',
      client_id: project.client_id || '',
      status: project.status || '',
      latitude: location?.latitude?.toString() || '',
      longitude: location?.longitude?.toString() || ''
    });
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    await supabase.from('projects').delete().eq('id', id);
    fetchProjects();
  };

  // Helper functions
  const getProjectInitials = (projectName) => {
    return projectName?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'PR';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'in progress': return '#00A884';
      case 'on hold': return '#ffc107';
      case 'complete': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'in progress': return 'autorenew';
      case 'on hold': return 'pause';
      case 'complete': return 'check_circle';
      default: return 'radio_button_unchecked';
    }
  };

  // Statistics
  const totalProjects = projects.length;
  const inProgressProjects = projects.filter(p => p.status === 'In Progress').length;
  const completedProjects = projects.filter(p => p.status === 'Complete').length;
  const mappedProjects = projects.filter(p => p.project_locations?.latitude && p.project_locations?.longitude).length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Table Loading */}
        <CardSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Project Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            folder
          </span>
          Project Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Projects"
              value={totalProjects}
              icon="folder"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="In Progress"
              value={inProgressProjects}
              icon="autorenew"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Completed"
              value={completedProjects}
              icon="check_circle"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Mapped Projects"
              value={mappedProjects}
              icon="map"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add Project Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                add_business
              </span>
              Project Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create, edit, and manage project information and locations
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => {
              setFormData({ id: null, name: '', project_number: '', client_id: '', status: 'In Progress', latitude: '', longitude: '' });
              setEditMode(false);
              setOpen(true);
            }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            Add Project
          </EnhancedButton>
        </Box>
      </Card>

      {/* Projects Table */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            view_list
          </span>
          Project Directory ({totalProjects})
        </Typography>
        
        <StyledTable
          columns={[
            { key: 'project_info', label: 'Project', width: 300 },
            { key: 'client', label: 'Client', width: 200 },
            { key: 'status', label: 'Status', width: 150 },
            { key: 'location', label: 'Location', width: 120 },
            { key: 'actions', label: 'Actions', width: 120 }
          ]}  
          rows={projects.map((project) => ({
            project_info: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: getStatusColor(project.status),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.875rem'
                  }}
                >
                  {getProjectInitials(project.name)}
                </Avatar>
                <Box>
                  <Typography level="title-sm" sx={{ color: 'success.700' }}>
                    {project.name || 'Unnamed Project'}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    #{project.project_number || 'No Number'}
                  </Typography>
                </Box>
              </Box>
            ),
            client: (
              <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                {clients.find(c => c.id === project.client_id)?.name || 'No Client'}
              </Typography>
            ),
            status: (
              <Chip 
                size="sm" 
                variant="soft" 
                sx={{
                  bgcolor: `${getStatusColor(project.status)}20`,
                  color: getStatusColor(project.status)
                }}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                    {getStatusIcon(project.status)}
                  </span>
                }
              >
                {project.status || 'Unknown'}
              </Chip>
            ),
            location: (
              <Box sx={{ textAlign: 'center' }}>
                {project.project_locations?.latitude && project.project_locations?.longitude ? (
                  <Chip size="sm" variant="soft" color="success" startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                      location_on
                    </span>
                  }>
                    Mapped
                  </Chip>
                ) : (
                  <Chip size="sm" variant="soft" color="neutral">
                    No Location
                  </Chip>
                )}
              </Box>
            ),
            actions: (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  size="sm" 
                  variant="outlined" 
                  onClick={() => handleEdit(project)}
                  sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    edit
                  </span>
                </IconButton>
                <IconButton 
                  size="sm" 
                  color="danger" 
                  variant="outlined" 
                  onClick={() => handleDelete(project.id)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                    delete
                  </span>
                </IconButton>
              </Box>
            )
          }))}
        />
      </Box>
      {/* Enhanced Project Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog 
          sx={{ 
            maxWidth: '700px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editMode ? 'edit' : 'add_business'}
            </span>
            {editMode ? 'Edit Project' : 'Add New Project'}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid xs={12}>
              <EnhancedInput
                label="Project Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Downtown Development Project"
                required
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    folder
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Project Number"
                name="project_number"
                value={formData.project_number}
                onChange={handleInputChange}
                placeholder="2024-001"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    tag
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Client</Typography>
                <Select
                  name="client_id"
                  value={formData.client_id}
                  onChange={(e, val) => setFormData(prev => ({ ...prev, client_id: val }))}
                  placeholder="Select client"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  {clients.map(client => (
                    <Option key={client.id} value={client.id}>{client.name}</Option>
                  ))}
                </Select>
              </Box>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Status</Typography>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={(e, val) => setFormData(prev => ({ ...prev, status: val }))}
                  placeholder="Select status"
                  sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                >
                  <Option value="In Progress">In Progress</Option>
                  <Option value="On Hold">On Hold</Option>
                  <Option value="Complete">Complete</Option>
                </Select>
              </Box>
            </Grid>
            
            <Grid xs={12}>
              <Typography level="body-sm" sx={{ mb: 2, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', fontFamily: 'Material Symbols Outlined' }}>
                  location_on
                </span>
                Project Location (Optional)
              </Typography>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Latitude"
                name="latitude"
                value={formData.latitude}
                onChange={handleInputChange}
                placeholder="40.7128"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    my_location
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Longitude"
                name="longitude"
                value={formData.longitude}
                onChange={handleInputChange}
                placeholder="-74.0060"
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                    explore
                  </span>
                }
              />
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSave}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      {editMode ? 'save' : 'add'}
                    </span>
                  }
                >
                  {editMode ? 'Update Project' : 'Add Project'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default ProjectManager;
